import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    useDisclosure,
    useColorMode,
    Textarea,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from "@chakra-ui/react";
import { FaSearch, FaPlus, FaTrash } from "react-icons/fa";
import { IconWrapper } from "../../components/IconWrapper";
import { getThemeClasses } from "../../utils/themeUtils";
import { db, auth } from "../../firebase/firebase";
import { logToSystem } from "../../utils/systemLogging";
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    deleteDoc,
    doc,
    setDoc,
    arrayUnion,
} from "firebase/firestore";
import { showToast } from "../../utils/toastUtils";

interface Department {
    id: string;
    name: string;
    description: string;
    headOfDepartment?: string;
    createdAt?: any;
}

interface DepartmentsProps {
    cardBg: string;
    textColor: string;
    organizationId: string;
    isLoading?: boolean;
}

const Departments: React.FC<DepartmentsProps> = ({
    cardBg,
    textColor,
    organizationId,
}) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const theme = getThemeClasses(isDark);

    const [departments, setDepartments] = useState<Department[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeleteAlertOpen,
        onOpen: onDeleteAlertOpen,
        onClose: onDeleteAlertClose,
    } = useDisclosure();
    const [newDepartment, setNewDepartment] = useState({
        name: "",
    });
    const [isSaving, setIsSaving] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const cancelRef = React.useRef<HTMLButtonElement>(null);

    const fetchDepartments = async () => {
        if (!organizationId) return;
        setIsLoading(true);
        try {
            const q = query(
                collection(db, `organization/${organizationId}/departments`),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(q);
            const deptList: Department[] = [];
            querySnapshot.forEach((doc) => {
                deptList.push({ id: doc.id, ...doc.data() } as Department);
            });
            setDepartments(deptList);
        } catch (error) {
            console.error("Error fetching departments:", error);
            showToast({
                title: "Error",
                description: "Failed to fetch departments.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
    }, [organizationId]);

    const handleAddDepartment = async () => {
        if (!newDepartment.name.trim()) {
            showToast({
                title: "Error",
                description: "Department name is required.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSaving(true);
        try {
            await addDoc(collection(db, `organization/${organizationId}/departments`), {
                ...newDepartment,
                createdAt: serverTimestamp(),
            });
            showToast({
                title: "Success",
                description: "Department added successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Log department creation activity
            try {
                const currentUserAuth = auth.currentUser;
                if (currentUserAuth && organizationId) {
                    const userRef = doc(db, "users", currentUserAuth.uid);
                    const userSnap = await getDoc(userRef);

                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        await logToSystem(organizationId, "actions", {
                            type: "ACTIVITY",
                            user: `${userData.firstName} ${userData.lastName}`,
                            email: userData.email,
                            action: "Department",
                            details: `Added department: ${newDepartment.name}`,
                            status: "success",
                        });
                    }
                }
            } catch (logError) {
                console.error("Error logging department creation:", logError);
            }

            onClose();
            setNewDepartment({ name: "" });
            fetchDepartments();
        } catch (error) {
            console.error("Error adding department:", error);
            showToast({
                title: "Error",
                description: "Failed to add department.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteDepartment = async () => {
        if (!departmentToDelete || !organizationId) return;

        // Find the department details before deleting
        const deptToDelete = departments.find(d => d.id === departmentToDelete);

        setIsDeleting(true);
        try {
            await deleteDoc(
                doc(db, `organization/${organizationId}/departments/${departmentToDelete}`)
            );

            showToast({
                title: "Success",
                description: "Department deleted successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Log department deletion activity
            try {
                const currentUserAuth = auth.currentUser;
                if (currentUserAuth && organizationId && deptToDelete) {
                    const userRef = doc(db, "users", currentUserAuth.uid);
                    const userSnap = await getDoc(userRef);

                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        await logToSystem(organizationId, "actions", {
                            type: "ACTIVITY",
                            user: `${userData.firstName} ${userData.lastName}`,
                            email: userData.email,
                            action: "Department",
                            details: `Deleted department ${deptToDelete.name}`,
                            status: "success",
                        });
                    }
                }
            } catch (logError) {
                console.error("Error logging department deletion:", logError);
            }

            onDeleteAlertClose();
            setDepartmentToDelete(null);
            fetchDepartments();
        } catch (error) {
            console.error("Error deleting department:", error);
            showToast({
                title: "Error",
                description: "Failed to delete department.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const confirmDeleteDepartment = (departmentId: string) => {
        setDepartmentToDelete(departmentId);
        onDeleteAlertOpen();
    };

    // Filter departments based on search query
    const filteredDepartments = departments.filter((dept) =>
        dept.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box>
            {/* Header */}
            <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
                {isDark && (
                    <>
                        <span className={theme.headerOverlayGlow} />
                        <span className={theme.headerOverlayTopLine} />
                        <span className={theme.headerOverlayBottomGlow} />
                        <span className={theme.headerOverlaySweep} />
                    </>
                )}
                <div className="flex flex-col space-y-1">
                    <span className={`text-2xl font-bold ${theme.text}`}>
                        Departments
                    </span>
                    <span className={`text-xs ${theme.textMuted}`}>
                        Manage organization departments
                    </span>
                </div>
                <Button
                    leftIcon={<IconWrapper icon={FaPlus} size={16} />}
                    bg={isDark ? "whiteAlpha.200" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    _hover={{ bg: isDark ? "whiteAlpha.300" : "gray.50" }}
                    size="md"
                    height="40px"
                    boxShadow="sm"
                    onClick={onOpen}
                    className="z-10"
                >
                    Add Department
                </Button>
            </div>

            {/* Table Container */}
            <div className={`w-full rounded-md ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
                {/* Search Bar */}
                <div className={`flex justify-between items-center px-2 py-3 ${theme.card} border-b ${theme.border} flex-wrap rounded-t-md`}>
                    <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <IconWrapper
                                icon={FaSearch}
                                className={`h-4 w-4 ${theme.textMuted}`}
                                size={16}
                            />
                        </div>
                        <input
                            type="text"
                            placeholder="Search departments..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                            className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
                        />
                    </div>
                </div>

                {isLoading && (
                    <div className={`absolute inset-0 flex items-center justify-center z-10 ${isDark ? 'bg-[#0B0B0F]/80' : 'bg-white/80'} backdrop-blur-sm`}>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    </div>
                )}

                <div className="px-5 overflow-auto" style={{ maxHeight: "68vh" }}>
                    {!isLoading && filteredDepartments.length === 0 ? (
                        <div className={`text-center py-8 col-span-full text-lg font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                            No departments found.
                        </div>
                    ) : (
                        <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                                <tr>
                                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                                        S.No
                                    </th>
                                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                                        Name
                                    </th>
                                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                                {filteredDepartments.map((dept, index) => (
                                    <tr key={dept.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#F2F4F7]'}`}>
                                        <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {index + 1}
                                        </td>
                                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {dept.name}
                                        </td>
                                        <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            <button
                                                onClick={() => confirmDeleteDepartment(dept.id)}
                                                className={`p-1.5 rounded-md transition-colors ${isDark
                                                    ? 'hover:bg-[#B59143]/20 text-[#FBD282] hover:text-[#B59143]'
                                                    : 'hover:bg-[#FFF7E6] text-[#B59143] hover:text-[#8B6914]'
                                                    }`}
                                                title="Delete Department"
                                            >
                                                <IconWrapper icon={FaTrash} size={14} className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

            </div>

            {/* Add Department Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent bg={cardBg} color={textColor} borderColor={isDark ? "#2E2F33" : "gray.200"} borderWidth="1px">
                    <ModalHeader>Add New Department</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl id="dept-name" isRequired mb={4}>
                            <FormLabel>Department Name</FormLabel>
                            <Input
                                placeholder="e.g. Engineering"
                                value={newDepartment.name}
                                onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <button
                            onClick={handleAddDepartment}
                            disabled={isSaving}
                            className={`flex items-center px-4 py-2 ${theme.primaryButton} rounded-md text-sm focus:outline-none ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteAlertClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={cardBg} color={textColor} borderColor={isDark ? "#2E2F33" : "gray.200"} borderWidth="1px">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Department
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete this department? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteAlertClose} variant="ghost">
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleDeleteDepartment}
                                ml={3}
                                isLoading={isDeleting}
                                loadingText="Deleting..."
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default Departments; // Force rebuild
